import { useLayoutEffect } from "react";
import type { ThemeSettings } from "../api/types";
import { APP_ROOT_ID } from "../app/useRail";
import { applyLanguage } from "../i18n";
import { LIGHT_COLORS, sameColors } from "./palettes";

const STYLE_ID = "hub-theme-vars";
const HEX = /^#[0-9a-fA-F]{6}$/;

const FONT_STACKS: Record<ThemeSettings["fontFamily"], string> = {
  inter: '"Inter", "Archivo", "Helvetica Neue", Helvetica, Arial, sans-serif',
  barlow_semi_condensed: '"Barlow Semi Condensed", "Bahnschrift", "DIN Alternate", sans-serif',
  poppins: '"Poppins", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
};

/** The API already validates hex on save; this only guards against writing something that
    is not a 6-digit hex color into a <style> tag's text content. */
function safeHex(value: string, fallback: string): string {
  return HEX.test(value) ? value : fallback;
}

function styleTag(): HTMLStyleElement {
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  return el;
}

export function applyFavicon(url: string | null): void {
  if (!url) return;
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.removeAttribute("type");
  link.href = url;
}

/** Writes the admin-chosen theme as CSS custom properties, applies the mode, orientation
    and font. Higher-specificity attribute selector (`[data-theme-runtime]`) beats the
    static `.surface-hub` / `.surface-hub.mode-dark` blocks in tokens.css regardless of
    stylesheet order, so this always wins once the theme has loaded. */
export function useAppliedTheme(theme: ThemeSettings | undefined): void {
  useLayoutEffect(() => {
    if (!theme) return;
    const c = theme.colors;
    // A dark console still holding the untouched light palette takes the built-in dark one.
    const paint = !(theme.mode === "dark" && sameColors(c, LIGHT_COLORS));
    styleTag().textContent = `
body.surface-hub[data-theme-runtime] {
${paint ? `  --bg: ${safeHex(c.bg, "#F5F7FA")};
  --bg-alt: ${safeHex(c.bgAlt, "#ECF0F6")};
  --surface: ${safeHex(c.surface, "#FFFFFF")};
  --line: ${safeHex(c.line, "#DFE5EE")};
  --text-hi: ${safeHex(c.textHi, "#181B31")};
  --text-mid: ${safeHex(c.textMid, "#181B31")};
  --text-lo: ${safeHex(c.textLo, "#181B31")};
  --accent: ${safeHex(c.accent, "#1F4585")};
  --accent-hi: ${safeHex(c.accentHi, "#16305E")};
  --accent-soft: color-mix(in srgb, ${safeHex(c.accent, "#1F4585")} ${theme.mode === "dark" ? 16 : 8.5}%, transparent);
  --accent-line: color-mix(in srgb, ${safeHex(c.accent, "#1F4585")} ${theme.mode === "dark" ? 40 : 26}%, transparent);
` : ""}
  --font-ui: ${FONT_STACKS[theme.fontFamily] ?? FONT_STACKS.poppins};
  --font-display: ${theme.fontFamily === "poppins" ? FONT_STACKS.poppins : FONT_STACKS.barlow_semi_condensed};
}`;
    document.body.setAttribute("data-theme-runtime", "1");
    document.body.classList.toggle("mode-dark", theme.mode === "dark");
    document.body.classList.toggle("mode-light", theme.mode === "light");
    document
      .getElementById(APP_ROOT_ID)
      ?.classList.toggle("is-horizontal", theme.sidebarOrientation === "horizontal");
    applyFavicon(theme.faviconUrl);
    applyLanguage(theme.defaultLanguage);
  }, [theme]);
}
